import IconOverflowSolid from '@apollo/icons/default/IconOverflowSolid.svg';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import { GraphQLSchema } from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import * as monacoEditor from 'monaco-editor';
import graphqlPlugin from 'prettier/plugins/graphql';
import { format } from 'prettier/standalone';
import React, { useCallback } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { executeEdits } from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/executeEdits';
import { GraphqlDefinition } from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import {
  getUsedVariablesString,
  getVariablesUsedInOperation,
} from 'src/app/graph/explorerPage/helpers/jsonHelpers';
import { useDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { processOperationHeaders } from 'src/app/graph/explorerPage/hooks/useExplorerState/performOperation/performOperation';
import { useHeadersManagerContext } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { sandboxGraphRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { EditorViewZone } from 'src/components/editorViewZone/EditorViewZone';
import { hotkeys } from 'src/hooks/useKeypressListener/useKeypressListener';
import Config from 'src/lib/config';
import { escapeShellArgument } from 'src/lib/escapeShellArgument';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { EditorBottomTab } from '../../../helpers/EditorBottomTab';
import { useCopyLinkToOperation } from '../../../hooks/useCopyLinkToOperation';
import { useEditorModel } from '../../../hooks/useEditorModel';
import { constructMultipartCurlString } from '../../../hooks/useExplorerState/performOperation/constructMultipartForm';
import { EditorTabState } from '../../../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { isHttpMultipart } from '../../../hooks/useExplorerState/useSubscriptions';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../../../hooks/usePerGraphIdentifierLocalStorage';
import { EditorRefactors } from '../../editorRefactors';

/**
 * A menu for for a defintion render at the end of the first line of the definition.
 */
export const DefinitionMenu = ({
  graphURL,
  editor,
  variablesEditor,
  schema,
  definition,
  setActiveEditorBottomTab,
  submitOperation,
  editorRefactorsRef,
  canBeEmbedded,
  shouldShowCopyLink,
  includeCookies,
  editorTabState,
}: {
  graphURL?: null | string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  definition: Pick<GraphqlDefinition, 'range' | 'kind'>;
  setActiveEditorBottomTab: (tab: EditorBottomTab) => void;
  submitOperation: (args: {
    operationName: string | undefined;
    body: string;
    isSubscription: boolean;
  }) => void;
  editorRefactorsRef: React.MutableRefObject<EditorRefactors | null>;
  canBeEmbedded: boolean;
  shouldShowCopyLink?: boolean;
  includeCookies: boolean;
  editorTabState: Pick<
    EditorTabState,
    'selectedTab' | 'tabbedFileVariableValues'
  >;
}) => {
  const definitionStartPosition = React.useMemo(
    () => definition.range.getStartPosition(),
    [definition],
  );
  const definitionDetails = useDefinitionDetails(
    editor,
    schema,
    definitionStartPosition,
  );

  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

  const lineNumber = definition.range.startLineNumber;

  const copyLinkToOperation = useCopyLinkToOperation();
  const handleCopyLinkToOperation = useCallback(async () => {
    await copyLinkToOperation({
      type: 'local',
      document: definitionDetails.activePartsOfDocument?.trim() || '',
      variables: getUsedVariablesString(
        schema,
        editor,
        variablesEditor,
        definitionStartPosition,
      ),
      headers: '',
      preflightOperationScript: '',
      postflightOperationScript: '',
    });
  }, [
    copyLinkToOperation,
    definitionDetails,
    definitionStartPosition,
    editor,
    schema,
    variablesEditor,
  ]);
  const model = useEditorModel(editor);
  const handleFormatDefinition = useCallback(async () => {
    if (!model) return;
    const startOffset = model.getOffsetAt(definition.range.getStartPosition());
    const leadingWhiteSpace =
      model
        .getValue()
        .slice(0, startOffset)
        .match(/(^|\n)[ \t]*$/)?.[0] ?? '';

    // Include leading whitespace in range so prettier can remove it.
    const formatRange = monacoEditor.Range.fromPositions(
      new monacoEditor.Position(
        definition.range.startLineNumber,
        definition.range.startColumn - leadingWhiteSpace.length,
      ),
      definition.range.getEndPosition(),
    );
    try {
      const formattedText = (
        await format(model.getValueInRange(formatRange), {
          parser: 'graphql',
          plugins: [graphqlPlugin],
        })
      ).trimEnd(); // prettier will add a newline at the end of the query, get rid of that.
      executeEdits(
        {
          type: 'format',
          variableEdits: [],
          edits: [{ text: formattedText, range: formatRange }],
        },
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
      );
    } catch {
      // failed to format text, do nothing.
      // This is likely caused by the query being invalid
    }
  }, [
    definition.range,
    editor,
    model,
    setActiveEditorBottomTab,
    variablesEditor,
  ]);

  const handleSubmit = useCallback(() => {
    if (!definitionDetails.isValid) return;
    submitOperation({
      operationName: definitionDetails.name ?? undefined,
      body: definitionDetails.activePartsOfDocument ?? '',
      isSubscription: definitionDetails.isSubscription ?? false,
    });
  }, [definitionDetails, submitOperation]);

  const handleCopyOperation = useCallback(
    () =>
      copy(
        definitionDetails.activeRanges
          ?.map((range) => model?.getValueInRange(range) ?? '')
          .join('\n\n') ?? '',
      ),
    [definitionDetails, model],
  );

  const { checkedHeaders, interpolateEnvVariablesIntoHeadersWithRef } =
    useHeadersManagerContext();

  const location = useLocation();
  const history = useHistory();

  const handleEmbedThisOperation = useCallback(() => {
    history.push({
      ...setOverlayVisibleLocation({
        location,
        overlayName: isSandboxRoute
          ? Config.modals.embedSandbox
          : Config.modals.embedExplorer,
      }),
      state: {
        embedModalTabState: {
          document: definitionDetails.activePartsOfDocument?.trim(),
          variables: getUsedVariablesString(
            schema,
            editor,
            variablesEditor,
            definitionStartPosition,
          ),
          headers: JSON.stringify(checkedHeaders),
          // Embedded explorer doesn't consume consume any includeCookies config options
          includeCookies: isSandboxRoute
            ? includeCookies.toString()
            : undefined,
        },
      },
    });
  }, [
    history,
    location,
    isSandboxRoute,
    definitionDetails,
    schema,
    editor,
    variablesEditor,
    definitionStartPosition,
    checkedHeaders,
    includeCookies,
  ]);

  const localStorageGraphIdentifierForSubscriptionProtocol =
    useLocalStorageGraphIdentifier({
      shouldNamespaceSandboxByEndpoint: true,
      shouldOnlyStoreForRegisteredGraphs: false,
    });
  const [protocol] = usePerGraphIdentifierLocalStorage({
    key: 'graphqlSubscriptionProtocol',
    graphIdentifier: localStorageGraphIdentifierForSubscriptionProtocol,
    stableInitialValue: 'auto-detect',
  });

  const getFileVariablesUsedInOperation = useCallback(() => {
    const variablesUsedInOperation = getVariablesUsedInOperation(
      schema,
      editor,
      definitionStartPosition,
    );
    const fileVariables = editorTabState.selectedTab
      ? editorTabState.tabbedFileVariableValues[editorTabState.selectedTab]
      : undefined;

    return fileVariables?.filter((variable) =>
      variablesUsedInOperation?.includes(variable.variableKey),
    );
  }, [
    definitionStartPosition,
    editor,
    editorTabState.selectedTab,
    editorTabState.tabbedFileVariableValues,
    schema,
  ]);

  const handleCopyOperationToCurl = useCallback(() => {
    const query =
      definitionDetails.activeRanges
        ?.map((range) => model?.getValueInRange(range) ?? '')
        .join('\n\n') ?? '';

    const fileVariables = getFileVariablesUsedInOperation();
    const variables = getUsedVariablesString(
      schema,
      editor,
      variablesEditor,
      definitionStartPosition,
    );

    const processedHeaders = processOperationHeaders(
      interpolateEnvVariablesIntoHeadersWithRef(checkedHeaders),
      query,
      !!(
        definitionDetails.isSubscription &&
        isHttpMultipart(protocol, graphURL ?? '')
      ),
      // for multipart form requests, the content type needs to have `multipart/form-data`
      // and the boundary https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type
      // the browser fills it in for us when we make it undefined,
      // since we don't know the boundary at this stage
      !isMinLengthArray(1, fileVariables),
    );

    if (isMinLengthArray(1, fileVariables)) {
      try {
        return copy(
          constructMultipartCurlString({
            processedHeaders,
            requestBody: {
              operationName: definitionDetails.name ?? undefined,
              query,
              variables: jsoncParser.parse(variables),
            },
            fileVariables,
            url: graphURL ?? undefined,
          }),
        );
      } catch {
        throw new Error(
          'Something went wrong parsing the variables, please try again.',
        );
      }
    } else {
      // Piping headers here seems ok for privacy because CURL is so transparent,
      // you wouldn't put it anywhere without realizing the headers are in there
      // and have an easy opportunity to take them out.
      const curlString = `curl --request POST \\${Object.entries(
        processedHeaders,
      )
        .map(
          ([key, value]) =>
            `\n    --header ${escapeShellArgument(`${key}: ${value}`)} \\`,
        )
        .join('')}
    --url ${escapeShellArgument(graphURL || '<GRAPH_URL>')} \\
    --data ${escapeShellArgument(
      JSON.stringify({
        query,
        variables: variables === '' ? undefined : jsoncParser.parse(variables),
      }),
    )}`;

      return copy(curlString);
    }
  }, [
    definitionDetails,
    getFileVariablesUsedInOperation,
    schema,
    editor,
    variablesEditor,
    definitionStartPosition,
    model,
    graphURL,
    interpolateEnvVariablesIntoHeadersWithRef,
    checkedHeaders,
    protocol,
  ]);

  return (
    <EditorViewZone className="z-50" editor={editor} lineNumber={lineNumber}>
      <div className="absolute right-0 mr-4 flex items-center">
        <div className="absolute inset-0 -mt-2 bg-primary-inverted opacity-75" />
        <Menu placement="bottom-end" autoSelect={false}>
          <MenuButton as={IconButton} size="sm" variant="hidden">
            <IconOverflowSolid />
          </MenuButton>
          <MenuContent>
            <MenuList>
              {shouldShowCopyLink && (
                <MenuItem onClick={handleCopyLinkToOperation}>
                  Copy link to {definition.kind}
                </MenuItem>
              )}
              <MenuItem onClick={handleCopyOperation}>
                Copy {definition.kind}
              </MenuItem>
              {definition.kind === 'operation' && (
                <MenuItem onClick={handleCopyOperationToCurl}>
                  Copy {definition.kind} to cURL
                </MenuItem>
              )}
              <MenuItem onClick={handleFormatDefinition}>
                Format {definition.kind}
              </MenuItem>
              <MenuItem
                onClick={() => editorRefactorsRef.current?.extractVariables()}
              >
                Extract Variables
              </MenuItem>
              <MenuItem
                onClick={() => editorRefactorsRef.current?.inlineVariables()}
              >
                Inline Variables
              </MenuItem>
              {definition.kind === 'operation' && (
                <Tooltip
                  label="Cannot run invalid operation"
                  disabled={definitionDetails.isValid}
                >
                  <MenuItem
                    isDisabled={!definitionDetails.isValid}
                    onClick={handleSubmit}
                    command={hotkeys.submitOperation.label}
                  >
                    Run operation
                  </MenuItem>
                </Tooltip>
              )}
              {canBeEmbedded && (
                <MenuItem
                  data-analytics-label="Embed Explorer CTA: Definition Menu"
                  data-analytics-category="Embeddable Explorer"
                  onClick={handleEmbedThisOperation}
                >
                  Embed this operation
                </MenuItem>
              )}
            </MenuList>
          </MenuContent>
        </Menu>
      </div>
    </EditorViewZone>
  );
};
